/**
 * Icon component for rendering SVG icons from @animus/iconic-svg.
 *
 * The icon inherits color from its parent via currentColor.
 * Size can be controlled via the size prop or custom styles.
 *
 * @example
 * import { Icon } from "@animus/primaris";
 * import { arrowUp } from "@animus/iconic-svg";
 *
 * <Icon svg={arrowUp} size="md" />
 * <Icon svg={arrowUp} xstyle={styles.customIcon} />
 */
import * as stylex from "@stylexjs/stylex";

const styles = stylex.create({
  base: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "inherit",
  },
  xs: { width: 12, height: 12 },
  sm: { width: 16, height: 16 },
  md: { width: 20, height: 20 },
  lg: { width: 24, height: 24 },
  xl: { width: 32, height: 32 },
  svg: {
    width: "100%",
    height: "100%",
  },
});

type IconSize = "xs" | "sm" | "md" | "lg" | "xl";

interface IconProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  svg: string;
  size?: IconSize;
  xstyle?: stylex.StyleXStyles;
  label?: string;
}

function Icon({ svg, size = "md", xstyle, label, ...props }: IconProps) {
  return (
    <span
      {...(label
        ? { role: "img", "aria-label": label }
        : { role: "presentation", "aria-hidden": true })}
      {...stylex.props(styles.base, styles[size], xstyle)}
      {...props}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG strings from iconic-svg are trusted
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export const icon = styles;
export { Icon, type IconProps, type IconSize };
